import { useCallback, useMemo, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Plus, Trash2 } from 'lucide-react';

import { Page, PageHeader, Panel, Toolbar } from '@/components/layout/Page';
import type { CandidateListItem } from '@/api/schema';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useCurrentUser } from '@/features/auth/api';
import { useDateFormat } from '@/features/company/api';
import { can } from '@/lib/permissions';

import { useCandidates, type CandidateFilters as Filters } from '../api';
import { COLUMNS, type CandidateSortKey } from '../columns';
import { AddCandidateChooser } from '../components/AddCandidateChooser';
import { ImportModal } from '../components/ImportModal';
import { CandidateFilters } from '../components/CandidateFilters';
import { CandidateTable } from '../components/CandidateTable';
import { CandidateDeleteDialog } from '../components/CandidateDeleteDialog';
import { ColumnChooser } from '../components/ColumnChooser';
import { useCandidateColumns } from '../useCandidateColumns';
import {
  DEFAULT_PER_PAGE,
  EMPTY_FILTERS,
  PER_PAGE_OPTIONS,
  parseFilters,
  toSearchParams,
} from '../urlState';

const PER_PAGE_SELECT = PER_PAGE_OPTIONS.map((size) => ({
  value: String(size),
  label: `${size} per page`,
}));

/**
 * The full candidate register.
 *
 * The query string is the state of this screen. Every filter, the sort and the page
 * live there and nowhere else. That is what makes a filtered view shareable by pasting
 * the address, and it is why the back button works without anything being written to
 * make it work. Column visibility is the one thing that is *not* in the URL: it is a
 * personal preference, so a shared link should arrive in the recipient's own layout.
 */
export function CandidateListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const { data: user } = useCurrentUser();
  const dateFormat = useDateFormat();

  // Safe: this page renders under RequireAuth, which holds while the session is
  // resolving and redirects when there is none.
  const { visible, toggle, reset, isDefault } = useCandidateColumns(user?.id ?? '');
  const columns = useMemo(
    () => COLUMNS.filter((column) => visible.includes(column.key)),
    [visible],
  );

  const query = useCandidates(filters);

  // Three states, not two: the chooser is a step in front of the form, so the CV
  // route can be explained rather than silently missing.
  const [adding, setAdding] = useState<'none' | 'choosing' | 'import'>('none');
  const canCreate = can(user, 'candidates', 'create');
  const canDelete = can(user, 'candidates', 'delete');
  const selectionKey = searchParams.toString();
  const [selection, setSelection] = useState<{ key: string; ids: string[] }>({
    key: '',
    ids: [],
  });
  const selectedCandidates =
    canDelete && selection.key === selectionKey
      ? (query.data?.items ?? []).filter((candidate) => selection.ids.includes(candidate.id))
      : [];
  const selectionReady = !query.isFetching && !query.isPlaceholderData && !query.isError;
  const [deleting, setDeleting] = useState<{
    candidates: CandidateListItem[];
    total: number;
  } | null>(null);

  /**
   * The single writer of the URL.
   *
   * The updater form is not stylistic: a debounced search can settle in the same tick
   * as a Select change, and reading the params out of the closure would silently drop
   * one of the two.
   */
  const apply = useCallback(
    (patch: Partial<Filters>, mode: 'push' | 'replace' = 'push') => {
      setSelection({ key: '', ids: [] });
      setSearchParams(
        (current) => {
          const next = { ...parseFilters(current), ...patch };
          // Anything that changes which rows match returns to the first page.
          // Narrowing to two results while on page seven shows an empty table.
          if (!('page' in patch)) next.page = 1;
          return toSearchParams(next);
        },
        // preventScrollReset, or sorting a table you have scrolled down throws you
        // back to the top of the page.
        { replace: mode === 'replace', preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const onSort = useCallback(
    (key: string) => {
      apply(
        filters.sort_by === key
          ? { sort_dir: filters.sort_dir === 'asc' ? 'desc' : 'asc' }
          : { sort_by: key as CandidateSortKey, sort_dir: 'asc' },
      );
    },
    [apply, filters.sort_by, filters.sort_dir],
  );

  const onOpen = useCallback(
    (id: string) => {
      // A useCallback rather than an inline arrow: the row is memoised, and an
      // unstable prop would undo that across a hundred rows.
      void navigate(`/candidates/${id}`, {
        // The list's whole state is in the query string, so handing it over is what
        // lets Back return to the view you left rather than to an unfiltered page one.
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search],
  );

  const onClear = useCallback(() => {
    // Clears the search and every filter, but keeps the sort, the page size and the
    // columns; those are how you are looking, not what you are looking for.
    const {
      per_page: _perPage,
      sort_by: _sortBy,
      sort_dir: _sortDir,
      ...cleared
    } = EMPTY_FILTERS;
    apply(cleared);
  }, [apply]);

  return (
    <Page>
      <PageHeader
        title="All candidates"
        description="The full register. Search, filter, and choose the columns you need."
        actions={
          canCreate ? (
            <Button
              icon={<Plus size={16} />}
              onClick={() => {
                setAdding('choosing');
              }}
            >
              Add candidate
            </Button>
          ) : null
        }
      />

      <Panel flush>
        <CandidateFilters
          filters={filters}
          onChange={(patch) => {
            // Search replaces rather than pushes: it settles once per typing pause,
            // and pushing those makes Back walk through half-typed words.
            apply(patch, 'search' in patch ? 'replace' : 'push');
          }}
          onClear={onClear}
          trailing={
            <>
              <Select
                aria-label="Rows per page"
                value={String(filters.per_page)}
                options={PER_PAGE_SELECT}
                onChange={(event) => {
                  apply({ per_page: Number(event.target.value) || DEFAULT_PER_PAGE });
                }}
              />
              <ColumnChooser
                visible={visible}
                onToggle={toggle}
                onReset={reset}
                isDefault={isDefault}
              />
            </>
          }
        />

        {selectedCandidates.length > 0 ? (
          <Toolbar>
            <span role="status">{selectedCandidates.length} selected</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelection({ key: '', ids: [] });
              }}
            >
              Clear selection
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<Trash2 size={16} />}
              disabled={!selectionReady}
              onClick={() => {
                setDeleting({ candidates: selectedCandidates, total: query.data?.total ?? 0 });
              }}
            >
              Delete selected ({selectedCandidates.length})
            </Button>
          </Toolbar>
        ) : null}

        <CandidateTable
          query={query}
          filters={filters}
          columns={columns}
          dateFormat={dateFormat}
          onSort={onSort}
          onClear={onClear}
          onRetry={() => {
            void query.refetch();
          }}
          onFirstPage={() => {
            apply({ page: 1 });
          }}
          onOpen={onOpen}
          selection={
            canDelete
              ? {
                  ids: selectedCandidates.map((candidate) => candidate.id),
                  disabled: !selectionReady,
                  onToggle: (id, checked) => {
                    setSelection({
                      key: selectionKey,
                      ids: checked
                        ? [...selectedCandidates.map((candidate) => candidate.id), id]
                        : selectedCandidates
                            .filter((candidate) => candidate.id !== id)
                            .map((candidate) => candidate.id),
                    });
                  },
                  onToggleAll: (checked) => {
                    setSelection({
                      key: selectionKey,
                      ids: checked
                        ? (query.data?.items ?? []).map((candidate) => candidate.id)
                        : [],
                    });
                  },
                  onDelete: (candidate) => {
                    setDeleting({ candidates: [candidate], total: query.data?.total ?? 0 });
                  },
                }
              : undefined
          }
        />

        {/* Renders nothing at all when the total is zero, so the empty states above
            are not sitting on top of an orphaned pager. */}
        <Pagination
          page={filters.page}
          perPage={filters.per_page}
          total={query.data?.total ?? 0}
          label="candidates"
          onChange={(page) => {
            apply({ page });
          }}
        />
      </Panel>

      {deleting ? (
        <CandidateDeleteDialog
          candidates={deleting.candidates}
          onClose={() => {
            setDeleting(null);
          }}
          onSuccess={(affected) => {
            setSelection({ key: '', ids: [] });
            const lastPage = Math.max(
              1,
              Math.ceil((deleting.total - affected) / filters.per_page),
            );
            if (filters.page > lastPage) apply({ page: lastPage });
          }}
        />
      ) : null}

      <AddCandidateChooser
        open={adding === 'choosing'}
        onClose={() => {
          setAdding('none');
        }}
        onManual={() => {
          setAdding('none');
          void navigate({ pathname: '/candidates/new', search: location.search });
        }}
        onImport={() => {
          setAdding('import');
        }}
      />

      {/* Mounted only while open so a second
          import should not open showing the last one's preview. */}
      {adding === 'import' ? (
        <ImportModal
          open
          onClose={() => {
            setAdding('none');
          }}
        />
      ) : null}
    </Page>
  );
}
